import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { FileText, IdCard, LogOut, Menu, type LucideIcon } from "lucide-react";

import { logoutFn, type CurrentUser } from "@/server/auth.functions";
import { loadMyCard } from "@/braining/partners.functions";
import type { ReqState } from "@/braining/partner-requisites";
import { BrainingLogo } from "@/braining/components/braining-logo";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

async function signOut() {
  await logoutFn();
  window.location.href = "/login";
}

/**
 * Оболочка кабинета партнёра — левое меню, как у сотрудников, но своё.
 *
 * Отдельная от панели сотрудника намеренно: подрядчику не нужно видеть, что
 * у агентства есть сметы, справочники и настройки, — даже пунктами меню,
 * ведущими в отказ. Здесь только его документы и его реквизиты; дальше
 * сюда встанет «Памятка» (этап 6).
 */
interface Item {
  to: "/cabinet" | "/cabinet/requisites";
  label: string;
  icon: LucideIcon;
  exact?: boolean;
}

const ITEMS: Item[] = [
  { to: "/cabinet", label: "Документы", icon: FileText, exact: true },
  { to: "/cabinet/requisites", label: "Реквизиты", icon: IdCard },
];

export function PartnerShell({
  user,
  children,
}: {
  user: CurrentUser | null;
  children: React.ReactNode;
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const reqState = useReqState(path);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-sidebar lg:flex">
        <Sidebar path={path} user={user} reqState={reqState} />
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-sidebar px-4 lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger
            className="text-sidebar-accent-foreground/80 hover:text-sidebar-accent-foreground"
            aria-label="Меню"
          >
            <Menu className="size-5" />
          </SheetTrigger>
          <SheetContent side="left" className="w-60 border-0 bg-sidebar p-0">
            <SheetTitle className="sr-only">Навигация</SheetTitle>
            <Sidebar
              path={path}
              user={user}
              reqState={reqState}
              onNavigate={() => setOpen(false)}
            />
          </SheetContent>
        </Sheet>
        <BrainingLogo className="h-7 w-auto text-sidebar-accent-foreground" />
      </header>

      <main className="px-4 py-6 sm:px-6 lg:py-8 lg:pl-[calc(15rem+2rem)] lg:pr-8">
        <div className="mx-auto max-w-3xl">{children}</div>
      </main>
    </div>
  );
}

/**
 * Где реквизиты — для отметки у пункта меню. Спрашиваем при каждом переходе:
 * Даша могла вернуть реквизиты, пока человек сидел на другой странице.
 */
function useReqState(path: string): ReqState | null {
  const [state, setState] = useState<ReqState | null>(null);
  useEffect(() => {
    let alive = true;
    loadMyCard()
      .then((c) => alive && setState(c?.requisites.state ?? null))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [path]);
  return state;
}

/** Отметка у «Реквизитов»: что требует действия человека. */
function reqMark(state: ReqState | null): { text: string; tone: "warn" | "main" } | null {
  if (state === "returned") return { text: "!", tone: "warn" };
  if (state === "draft") return { text: "заполнить", tone: "main" };
  return null;
}

function Sidebar({
  path,
  user,
  reqState,
  onNavigate,
}: {
  path: string;
  user: CurrentUser | null;
  reqState: ReqState | null;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5">
        <BrainingLogo className="h-8 w-auto text-sidebar-accent-foreground" />
        <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.12em] text-sidebar-foreground">
          Кабинет партнёра
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3">
        {ITEMS.map((item) => {
          const active = item.exact ? path === item.to : path.startsWith(item.to);
          const mark = item.to === "/cabinet/requisites" ? reqMark(reqState) : null;
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                active
                  ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                  : "text-sidebar-accent-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
              )}
            >
              <item.icon
                className={cn(
                  "size-4 shrink-0",
                  active ? "text-sidebar-primary" : "text-sidebar-foreground",
                )}
              />
              {item.label}
              {mark && (
                <span
                  className={cn(
                    "ml-auto rounded-full px-1.5 py-0.5 text-[11px] font-medium",
                    mark.tone === "warn"
                      ? "bg-warning text-warning-foreground"
                      : "bg-sidebar-primary text-sidebar",
                  )}
                >
                  {mark.text}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        {user && (
          <div className="flex items-center gap-2 px-1">
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs text-sidebar-accent-foreground">{user.email}</div>
              <div className="text-[11px] text-sidebar-foreground">Партнёр</div>
            </div>
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label="Выйти"
              className="shrink-0 rounded-md p-1.5 text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
